import {
	PlanNameType,
	PaymentPeriodType,
	Plan,
	Price,
} from '../../../../lib/api/types/billing.interfaces'
import type { RegionalPlanProduct } from '../../../../lib/api/types/pricing.interfaces'

export const getPlanMinimumUsers = (
	planName: string,
	minUser?: number,
): number => {
	if (minUser != null && Number.isFinite(minUser) && minUser > 0) {
		return minUser
	}
	return planName === 'Business' ? 3 : 5
}

export const getMinimumUsers = (
	planName: string,
	minUser?: number,
	totalUsers?: number,
): number => Math.max(getPlanMinimumUsers(planName, minUser), totalUsers ?? 0)

export const parsePlanName = (planString?: string): PlanNameType => {
	if (!planString) return 'Free' as PlanNameType

	const match = planString.match(/^(.+?)\s*\(.+?\)$/)
	if (match) return match[1].trim() as PlanNameType

	return planString as PlanNameType
}

export const isCurrentPurchasedPlan = (
	planName: string,
	paymentPeriod: PaymentPeriodType,
	currentPlan?: Plan,
	selectedPriceId?: string,
	currentPriceId?: string,
): boolean => {
	const actualCurrentPriceId = currentPlan?.priceId ?? currentPriceId

	// Subscription priceId can differ from catalog priceId after quota changes — only short-circuit on match.
	if (
		actualCurrentPriceId &&
		selectedPriceId &&
		actualCurrentPriceId === selectedPriceId
	) {
		return true
	}

	if (!currentPlan) return false

	const currentPlanName = parsePlanName(currentPlan.name)
	const parsedPlanName = parsePlanName(planName)

	if (parsedPlanName === 'Free' || currentPlanName === 'Free') {
		return parsedPlanName === currentPlanName
	}

	const currentPlanPeriod = (currentPlan.paymentPeriod ||
		'month') as PaymentPeriodType
	return (
		parsedPlanName === currentPlanName && paymentPeriod === currentPlanPeriod
	)
}

export const getPriceForPeriod = (
	prices: Price[] | undefined,
	period: PaymentPeriodType,
): Price | undefined =>
	prices?.find((price) => price.recurring.interval === period)

export const getPriceIdForPeriod = (
	prices: Price[] | undefined,
	period: PaymentPeriodType,
): string | undefined => getPriceForPeriod(prices, period)?.id

export const getDisplayPriceForPeriod = (
	plan: RegionalPlanProduct,
	period: PaymentPeriodType,
): number | undefined =>
	period === 'month'
		? plan.regionalPricing.monthly
		: plan.regionalPricing.annual

const TB_IN_BYTES = 1024 ** 4
const DEFAULT_PRO_STORAGE_BYTES = TB_IN_BYTES

export const DEFAULT_STORAGE_PER_USER_BYTES: Record<
	'Business' | 'Enterprise',
	number
> = {
	Business: 0.5 * TB_IN_BYTES,
	Enterprise: 1 * TB_IN_BYTES,
}

const isValidPositiveNumber = (value?: number | null): value is number =>
	value != null && Number.isFinite(value) && value > 0

export const getStoragePerUserBytes = (
	planName: string,
	storagePerUser?: number | null,
): number | null => {
	if (isValidPositiveNumber(storagePerUser)) {
		return storagePerUser
	}
	if (planName === 'Business' || planName === 'Enterprise') {
		return DEFAULT_STORAGE_PER_USER_BYTES[planName]
	}
	return null
}

export const getComputedStorageBytes = (
	planName: string,
	users?: number,
	storagePerUser?: number | null,
	maxStorage?: number | null,
): number => {
	if (planName === 'Free') return 0

	if (planName === 'Pro') {
		if (isValidPositiveNumber(storagePerUser)) {
			return storagePerUser
		}
		if (isValidPositiveNumber(maxStorage)) {
			return maxStorage
		}
		return DEFAULT_PRO_STORAGE_BYTES
	}

	const perUserBytes = getStoragePerUserBytes(planName, storagePerUser)
	if (perUserBytes == null) {
		return isValidPositiveNumber(maxStorage) ? maxStorage : 0
	}

	const safeUsers = Math.max(1, Number(users) || 1)
	return safeUsers * perUserBytes
}

export const getComputedStorageTB = (
	planName: string,
	users?: number,
	storagePerUser?: number | null,
	maxStorage?: number | null,
): number => {
	const bytes = getComputedStorageBytes(
		planName,
		users,
		storagePerUser,
		maxStorage,
	)
	return Math.round((bytes / TB_IN_BYTES) * 100) / 100
}

export const getUsersValidationError = (
	planName: string,
	value: number | string,
	totalUsers?: number,
	minUser?: number,
): string | null => {
	const planMinUsers = getPlanMinimumUsers(planName, minUser)
	const effectiveMinUsers = getMinimumUsers(planName, minUser, totalUsers)
	const numericValue = Number(value)

	if (!Number.isFinite(numericValue) || numericValue < effectiveMinUsers) {
		if (totalUsers != null && numericValue < totalUsers) {
			return `Your organization already has ${totalUsers} users. You cannot select fewer than ${totalUsers} users.`
		}
		return `The minimum for the ${planName} plan is ${planMinUsers} users.`
	}

	return null
}

export const isPlanDowngrade = (
	newPlan: string,
	currentPlan?: Plan,
): boolean => {
	if (!currentPlan) return false

	const currentPlanName = parsePlanName(currentPlan.name)
	const planHierarchy = { Free: 0, Pro: 1, Business: 2, Enterprise: 3 }
	return (
		planHierarchy[newPlan as keyof typeof planHierarchy] <
		planHierarchy[currentPlanName as keyof typeof planHierarchy]
	)
}
